import { ToggleGroup, ToggleGroupItem } from 'components/ToggleGroup';
import { Typography } from 'components/typography';
import { cn } from 'utils/shadcn';

export type SegmentedToggleOption<T extends string> = {
  value: T;
  label: string;
  ariaLabel: string;
};

type SegmentedToggleProps<T extends string> = {
  value: T;
  options: readonly [SegmentedToggleOption<T>, SegmentedToggleOption<T>];
  onChange: (value: T) => void;
  className?: string;
};

// Shared styling for two-option segmented controls.
const TOGGLE_GROUP_CLASSNAME = cn(
  'relative m-0 grid h-9 w-full grid-cols-2 gap-0',
  'rounded-md border border-neutral-300 bg-neutral-100 p-0.5'
);

const TOGGLE_ITEM_CLASSNAME = cn(
  'relative z-10 flex h-8 min-h-0 min-w-0 flex-1 items-center justify-center px-2 py-0',
  '!bg-transparent border-0 shadow-none text-xs font-medium',
  'data-[state=on]:!bg-transparent data-[state=off]:!bg-transparent',
  'data-[state=on]:text-neutral-50 data-[state=off]:text-neutral-600',
  'hover:!bg-transparent hover:text-neutral-600',
  'data-[state=on]:hover:text-neutral-50'
);

export function SegmentedToggle<T extends string>({
  value,
  options,
  onChange,
  className,
}: SegmentedToggleProps<T>) {
  const isSecondSelected = value === options[1].value;

  return (
    <ToggleGroup
      type='single'
      value={value}
      onValueChange={(nextValue: string) => {
        const option = options.find((item) => item.value === nextValue);
        if (option) {
          onChange(option.value);
        }
      }}
      className={cn(TOGGLE_GROUP_CLASSNAME, className)}
    >
      <div
        className={cn(
          'absolute top-0.5 bottom-0.5 w-[calc(50%-2px)] rounded-[4px]',
          'bg-brand-main transition-all duration-200 ease-in-out',
          isSecondSelected ? 'left-[calc(50%+1px)]' : 'left-0.5'
        )}
      />
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          aria-label={option.ariaLabel}
          className={TOGGLE_ITEM_CLASSNAME}
        >
          <Typography
            as='span'
            variant='body-xs'
            weight='medium'
            className='text-inherit'
          >
            {option.label}
          </Typography>
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
